/*
 * Periodo y filtros del tablero de Marcaciones (funciones puras: sin React y sin red).
 *
 * El estado de la pantalla vive en la URL (?seccion=&periodo=&fecha=&desde=&hasta=&zona=&tienda=&empleado=)
 * para poder compartir el enlace y volver atrás sin perder lo elegido. Uso típico (ver ../index.js):
 *     const filters = readFilters(location.search, todayISO());
 *     const range = periodRange(filters);               // {desde, hasta} en 'AAAA-MM-DD'
 *     const params = buildParams(filters, range);       // query de /attendance/jornadas/ y /horas/
 *
 * Notas no obvias:
 * - Las fechas viajan como texto 'AAAA-MM-DD' y se calculan en UTC para no depender de la zona horaria
 *   del navegador. Solo todayISO() lee el reloj LOCAL (el día de hoy en Ecuador; toISOString() daría
 *   el día siguiente después de las 19:00).
 * - Hoy / Semana (lunes a domingo) / Mes se anclan en `fecha`; Personalizado usa `desde` y `hasta`.
 *   Las flechas mueven el periodo completo; Personalizado se mueve en bloques de su misma duración.
 * - MAX_RANGE_DAYS repite el límite del servidor (spec sección 5) para avisar antes de llamar.
 */
export const SECTIONS = ['ingresos', 'almuerzos', 'salidas', 'horas', 'dashboard'];
export const PERIODS = ['hoy', 'semana', 'mes', 'personalizado'];
export const MAX_RANGE_DAYS = 366;
export const MAX_EXPORT_DAYS = 93;

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre',
    'Octubre', 'Noviembre', 'Diciembre'];
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const WEEKDAYS_SHORT = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86400000;

const pad = (value) => String(value).padStart(2, '0');

function toUTC(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}

function fromUTC(date) {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Fecha LOCAL de un Date como 'AAAA-MM-DD'. */
export function toISODate(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Hoy en hora local del navegador. */
export function todayISO(now = new Date()) {
    return toISODate(now);
}

/** true si el texto es una fecha real con formato 'AAAA-MM-DD' (rechaza 2026-02-30). */
export function isValidISO(value) {
    if (!ISO_DATE.test(value || '')) return false;
    return fromUTC(toUTC(value)) === value;
}

export function addDays(iso, days) {
    const date = toUTC(iso);
    date.setUTCDate(date.getUTCDate() + days);
    return fromUTC(date);
}

/** 0 = lunes ... 6 = domingo (igual que Python date.weekday()). */
export function weekdayIndex(iso) {
    return (toUTC(iso).getUTCDay() + 6) % 7;
}

/** Cantidad de días entre dos fechas, ambas incluidas. */
export function daysBetween(from, to) {
    return Math.round((toUTC(to) - toUTC(from)) / DAY_MS) + 1;
}

export function startOfWeek(iso) {
    return addDays(iso, -weekdayIndex(iso));
}

export function startOfMonth(iso) {
    return `${iso.slice(0, 8)}01`;
}

export function endOfMonth(iso) {
    const date = toUTC(startOfMonth(iso));
    date.setUTCMonth(date.getUTCMonth() + 1);
    date.setUTCDate(0);
    return fromUTC(date);
}

function addMonths(iso, months) {
    const date = toUTC(startOfMonth(iso));
    date.setUTCMonth(date.getUTCMonth() + months);
    return fromUTC(date);
}

/** Rango {desde, hasta} (ambos incluidos) del periodo elegido. */
export function periodRange(filters) {
    if (filters.periodo === 'semana') {
        const start = startOfWeek(filters.fecha);
        return {desde: start, hasta: addDays(start, 6)};
    }
    if (filters.periodo === 'mes') {
        return {desde: startOfMonth(filters.fecha), hasta: endOfMonth(filters.fecha)};
    }
    if (filters.periodo === 'personalizado') {
        return {desde: filters.desde, hasta: filters.hasta};
    }
    return {desde: filters.fecha, hasta: filters.fecha};
}

/** Mueve el periodo `step` veces (negativo = hacia atrás). */
export function shiftPeriod(filters, step) {
    if (filters.periodo === 'semana') return {...filters, fecha: addDays(filters.fecha, 7 * step)};
    if (filters.periodo === 'mes') return {...filters, fecha: addMonths(filters.fecha, step)};
    if (filters.periodo === 'personalizado') {
        const length = daysBetween(filters.desde, filters.hasta);
        return {...filters, desde: addDays(filters.desde, length * step), hasta: addDays(filters.hasta, length * step)};
    }
    return {...filters, fecha: addDays(filters.fecha, step)};
}

/** Se puede avanzar solo si el periodo siguiente empieza hoy o antes. */
export function canGoForward(filters, today) {
    return periodRange(shiftPeriod(filters, 1)).desde <= today;
}

/** Hoy / Semana / Mes se anclan en el día de hoy; Personalizado arranca con el rango que se estaba viendo. */
export function changePeriod(filters, periodo, today) {
    if (periodo === 'personalizado') {
        const range = periodRange(filters);
        return {...filters, periodo, desde: range.desde, hasta: range.hasta};
    }
    return {...filters, periodo, fecha: today};
}

/** Mensaje para el usuario si el rango no se puede consultar, o null si es válido. */
export function rangeError(range) {
    if (!isValidISO(range.desde) || !isValidISO(range.hasta)) return 'Elige una fecha de inicio y una de fin.';
    if (range.desde > range.hasta) return 'La fecha de inicio no puede ser posterior a la fecha de fin.';
    if (daysBetween(range.desde, range.hasta) > MAX_RANGE_DAYS) return `El rango máximo es de ${MAX_RANGE_DAYS} días.`;
    return null;
}

/** Aviso si el rango pasa de 93 días (límite del dashboard y de la exportación); `what` = 'La exportación' o 'El dashboard'. */
export function longRangeMessage(range, what) {
    if (!isValidISO(range.desde) || !isValidISO(range.hasta)) return null;
    return daysBetween(range.desde, range.hasta) > MAX_EXPORT_DAYS ? `${what} admite rangos de hasta ${MAX_EXPORT_DAYS} días` : null;
}

/** 'lun 21 sep' */
export function shortDayLabel(iso) {
    const [, month, day] = iso.split('-').map(Number);
    return `${WEEKDAYS_SHORT[weekdayIndex(iso)]} ${day} ${MONTHS_SHORT[month - 1]}`;
}

function shortDate(iso, withYear) {
    const [year, month, day] = iso.split('-').map(Number);
    return withYear ? `${day} ${MONTHS_SHORT[month - 1]} ${year}` : `${day} ${MONTHS_SHORT[month - 1]}`;
}

/** Texto del periodo para la barra de filtros: 'Hoy', 'Ayer', 'lun 21 sep', '21 sep – 27 sep', 'Septiembre 2026'. */
export function periodLabel(filters, today) {
    const range = periodRange(filters);
    if (filters.periodo === 'hoy') {
        if (range.desde === today) return 'Hoy';
        if (range.desde === addDays(today, -1)) return 'Ayer';
        return shortDayLabel(range.desde);
    }
    if (filters.periodo === 'mes') {
        const [year, month] = range.desde.split('-').map(Number);
        return `${MONTHS[month - 1]} ${year}`;
    }
    const withYear = range.desde.slice(0, 4) !== range.hasta.slice(0, 4);
    return `${shortDate(range.desde, withYear)} – ${shortDate(range.hasta, withYear)}`;
}

/** Filtros desde la URL, con valores por defecto para lo que falte o no sea válido. */
export function readFilters(search, today) {
    const query = new URLSearchParams(search);
    const pick = (name, allowed, fallback) => (allowed.includes(query.get(name)) ? query.get(name) : fallback);
    const date = (name, fallback) => (isValidISO(query.get(name)) ? query.get(name) : fallback);
    const id = (name) => (/^\d+$/.test(query.get(name) || '') ? query.get(name) : '');
    const fecha = date('fecha', today);
    return {
        seccion: pick('seccion', SECTIONS, SECTIONS[0]),
        periodo: pick('periodo', PERIODS, 'hoy'),
        fecha,
        desde: date('desde', fecha),
        hasta: date('hasta', fecha),
        zona: id('zona'),
        tienda: id('tienda'),
        empleado: id('empleado'),
    };
}

/** Filtros a query string ('?seccion=...'). Solo guarda lo que aplica al periodo elegido. */
export function writeFilters(filters) {
    const query = new URLSearchParams();
    query.set('seccion', filters.seccion);
    query.set('periodo', filters.periodo);
    if (filters.periodo === 'personalizado') {
        query.set('desde', filters.desde);
        query.set('hasta', filters.hasta);
    } else {
        query.set('fecha', filters.fecha);
    }
    ['zona', 'tienda', 'empleado'].forEach((key) => {
        if (filters[key]) query.set(key, filters[key]);
    });
    return `?${query.toString()}`;
}

/** Parámetros para la API (nombres del backend); omite los filtros vacíos. */
export function buildParams(filters, range, extra = {}) {
    const params = {fecha_desde: range.desde, fecha_hasta: range.hasta};
    if (filters.zona) params.id_zona = filters.zona;
    if (filters.tienda) params.id_tienda = filters.tienda;
    if (filters.empleado) params.id_empleado = filters.empleado;
    return {...params, ...extra};
}

/** Texto del periodo de una fila de /attendance/horas/: 'lun 21 sep', 'Semana del lun 21 sep', 'Septiembre 2026'. */
export function groupLabel(periodStart, grouping) {
    if (grouping === 'mes') {
        const [year, month] = periodStart.split('-').map(Number);
        return `${MONTHS[month - 1]} ${year}`;
    }
    if (grouping === 'semana') return `Semana del ${shortDayLabel(periodStart)}`;
    return shortDayLabel(periodStart);
}

/** Agrupación inicial de Horas trabajadas según el periodo (Hoy: por día; el resto: por semana). */
export function defaultGrouping(periodo) {
    return periodo === 'hoy' ? 'dia' : 'semana';
}

/** Mes 'AAAA-MM' de la ficha del empleado desde la URL (?mes=); por defecto (o si es futuro o inválido), el mes de hoy. */
export function readMonth(search, today) {
    const value = new URLSearchParams(search).get('mes') || '';
    const valid = /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
    return valid && value <= today.slice(0, 7) ? value : today.slice(0, 7);
}

/** Rango {desde, hasta} de un mes 'AAAA-MM'. */
export function monthRange(month) {
    const first = `${month}-01`;
    return {desde: first, hasta: endOfMonth(first)};
}

/** Mes 'AAAA-MM' movido `step` meses. */
export function shiftMonth(month, step) {
    return addMonths(`${month}-01`, step).slice(0, 7);
}

/** 'Septiembre 2026' */
export function monthLabel(month) {
    const [year, number] = month.split('-').map(Number);
    return `${MONTHS[number - 1]} ${year}`;
}

/** Id de empleado de la URL: entero positivo int32 (sin ceros a la izquierda ni signos) o null. */
export function parseEmployeeId(value) {
    if (!/^[1-9]\d{0,9}$/.test(String(value))) return null;
    const number = Number(value);
    return number <= 2147483647 ? number : null;
}
